import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type StudentDetail } from '../lib/api';
import { ErrorNote, SectionHeading, Spinner } from '../components/ui';
import { Related } from '../components/Related';
import { DeleteRecord } from '../components/DeleteRecord';
import { Confirm } from '../components/Confirm';
import { useIsPlatformAdmin } from '../lib/useSession';

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * One student, and the classes they are on the roll of.
 *
 * The other half of a class's roll. Enrolling used to be possible only from the
 * class, so putting a new student into three classes meant visiting three
 * pages, and nothing anywhere answered "which classes is this student in".
 *
 * Enrolling several classes at once is one request, the same route the roll
 * uses with the pair transposed, so a failure part way through cannot leave the
 * student in some of them.
 */
export function ManageStudentDetail() {
  const { studentId } = useParams({ from: '/app/manage/students/$studentId' });
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['student', studentId],
    queryFn: () => api.admin.student(studentId),
  });
  const classes = useQuery({ queryKey: ['classes'], queryFn: api.classes.list });

  /*
   * An enrolment is a row of somebody's register, so changing one moves more
   * than this page. ['classes'] carries studentCount, ['enrollments'] is the
   * class's own roll, and ['grid'] is the marking screen whose rows this is.
   * The last two are matched by prefix across every class and date cached.
   */
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['student', studentId] }),
      queryClient.invalidateQueries({ queryKey: ['students'] }),
      queryClient.invalidateQueries({ queryKey: ['classes'] }),
      queryClient.invalidateQueries({ queryKey: ['enrollments'] }),
      queryClient.invalidateQueries({ queryKey: ['grid'] }),
    ]);
  };

  const [startDate, setStartDate] = useState(today());
  const [query, setQuery] = useState('');
  const [showLeft, setShowLeft] = useState(false);
  const [undo, setUndo] = useState<{ message: string; id: string } | null>(null);

  const enrol = useMutation({
    mutationFn: (classIds: string[]) => api.admin.enrollMany({ studentId, classIds, startDate }),
    onSuccess: async () => {
      setQuery('');
      await refresh();
    },
  });

  const end = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string | null }) =>
      api.admin.endEnrollment(id, date),
    onSuccess: refresh,
  });

  if (detail.isLoading) return <Spinner />;
  if (detail.isError || !detail.data) {
    return (
      <div>
        <Link to="/manage/students" className="eyebrow">
          Back to students
        </Link>
        <div className="mt-4">
          <ErrorNote message={message(detail.error, 'Could not load this student.')} />
        </div>
      </div>
    );
  }

  const student = detail.data;
  const enrolled = student.enrollments;
  const open = enrolled.filter((e) => !e.endDate);
  const openIds = new Set(open.map((e) => e.classId));
  const leftIds = new Set(enrolled.filter((e) => e.endDate).map((e) => e.classId));

  /*
   * Retired classes are left out, matching the register: a class nobody will
   * mark again is not somewhere to enrol a student. They stay visible in the
   * standing list above, because the student really was on that roll.
   */
  const candidates = (classes.data?.classes ?? [])
    .filter((klass) => klass.isActive && !openIds.has(klass.id))
    .filter((klass) => showLeft || !leftIds.has(klass.id))
    .map((klass) => {
      const previous = enrolled.find((e) => e.classId === klass.id && e.endDate);
      return {
        id: klass.id,
        label: klass.name,
        detail: previous ? `left ${previous.endDate}` : undefined,
        muted: Boolean(previous),
      };
    });

  return (
    <div className="grid gap-8">
      <div>
        <Link to="/manage/students" className="eyebrow hover:text-ink">
          Back to students
        </Link>
        <div className="mt-2">
          <StudentHeader student={student} onDone={refresh} />
        </div>
      </div>

      <Related
        title="On the roll of"
        description="Enrolments are dated rather than a membership flag, so ending one keeps the student on every register they were already marked on."
        count={`${open.length} current`}
        error={end.error ? message(end.error, 'Could not change that enrolment.') : null}
        undo={
          undo
            ? {
                message: undo.message,
                onUndo: () => {
                  end.mutate({ id: undo.id, date: null });
                  setUndo(null);
                },
                onDismiss: () => setUndo(null),
              }
            : null
        }
        label="Add to a class"
        options={candidates}
        query={query}
        onQuery={setQuery}
        empty={
          showLeft
            ? 'Already on the roll of every active class.'
            : 'Already on the roll of every active class, or has left it.'
        }
        searchPlaceholder="class name"
        disabled={enrol.isPending}
        filters={
          <label className="flex items-center gap-2 pb-2 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={showLeft}
              onChange={(e) => setShowLeft(e.target.checked)}
            />
            include classes they have left
          </label>
        }
        verb="Enrol"
        applying={enrol.isPending}
        actions={
          <label className="grid gap-1.5">
            <span className="eyebrow">From</span>
            <input
              type="date"
              className="field w-auto font-mono"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
        }
        note={
          enrol.error ? (
            <div className="mt-3">
              <ErrorNote message={message(enrol.error, 'Could not enrol.')} />
            </div>
          ) : null
        }
        onApply={(ids, done) => {
          setUndo(null);
          enrol.mutate(ids, { onSuccess: done });
        }}
      >
        <ul className="mb-4">
          {enrolled.map((enrolment) => (
            <li
              key={enrolment.id}
              className="flex flex-wrap items-center gap-3 border-b border-rule py-1.5 text-sm last:border-0"
            >
              <Link
                to="/manage/classes/$classId"
                params={{ classId: enrolment.classId }}
                className={
                  enrolment.endDate
                    ? 'text-ink-faint line-through hover:text-ink'
                    : 'hover:underline'
                }
              >
                {enrolment.className}
              </Link>
              {enrolment.classIsActive ? null : <span className="eyebrow">retired class</span>}
              <span className="ml-auto font-mono text-xs text-ink-soft">
                {enrolment.endDate ? `left ${enrolment.endDate}` : `since ${enrolment.startDate}`}
              </span>
              {enrolment.endDate ? null : (
                <Confirm
                  trigger="end enrolment"
                  triggerClassName="font-mono text-xs text-ink-faint underline hover:text-ink"
                  title={`End ${student.firstName}'s enrolment in ${enrolment.className}?`}
                  description="They stay on every register they were already marked on, and leave the ones after the day chosen here."
                  body={
                    <label className="grid gap-1.5">
                      <span className="eyebrow">Last day</span>
                      <input
                        name="date"
                        type="date"
                        className="field w-auto font-mono"
                        defaultValue={today()}
                        required
                      />
                    </label>
                  }
                  confirmLabel="End"
                  pendingLabel="Ending…"
                  disabled={end.isPending}
                  onConfirm={async (form) => {
                    const date = String(form.get('date'));
                    await end.mutateAsync({ id: enrolment.id, date });
                    setUndo({
                      message: `Ended ${student.firstName}'s enrolment in ${enrolment.className} on ${date}.`,
                      id: enrolment.id,
                    });
                  }}
                  errorFallback="Could not end that enrolment."
                />
              )}
            </li>
          ))}
          {enrolled.length === 0 ? (
            <li className="py-1.5 text-sm text-ink-soft">Not on any class's roll yet.</li>
          ) : null}
        </ul>
      </Related>
    </div>
  );
}

/**
 * The student's own details, edited in place.
 *
 * This used to live in the Students list, under the row, reached through an
 * Edit button in the last column. One record with two places to edit it is the
 * confusion this page exists to remove, so the list now links here instead.
 */
function StudentHeader({ student, onDone }: { student: StudentDetail; onDone: () => void }) {
  const navigate = useNavigate();
  const canRestore = useIsPlatformAdmin();
  const [firstName, setFirstName] = useState(student.firstName);
  const [lastName, setLastName] = useState(student.lastName);
  const [guardianName, setGuardianName] = useState(student.guardianName ?? '');
  const [guardianContact, setGuardianContact] = useState(student.guardianContact ?? '');

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.admin.updateStudent(student.id, body),
    onSuccess: onDone,
  });

  /* Separate from `save`: a deleted student is no longer visible to the person
     who deleted them, so staying here would refetch into a 404. */
  const remove = useMutation({
    mutationFn: (isDeleted: boolean) => api.admin.updateStudent(student.id, { isDeleted }),
    onSuccess: async (_result, isDeleted) => {
      onDone();
      if (isDeleted) await navigate({ to: '/manage/students' });
    },
  });

  const dirty =
    firstName !== student.firstName ||
    lastName !== student.lastName ||
    guardianName !== (student.guardianName ?? '') ||
    guardianContact !== (student.guardianContact ?? '');

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-1.5">Student</p>
          <h1 className="text-[28px] leading-none font-semibold tracking-[-0.02em]">
            {student.firstName} {student.lastName}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {student.isActive || student.deletedAt ? null : (
            <span className="eyebrow">off the roll</span>
          )}
          {student.deletedAt ? null : student.isActive ? (
            <Confirm
              trigger="Take off the roll"
              title={`Take ${student.firstName} ${student.lastName} off the roll?`}
              description="They leave every register from today. Marks already made are kept, and they can be put back."
              confirmLabel="Take off the roll"
              pendingLabel="Updating…"
              disabled={save.isPending}
              onConfirm={() => save.mutateAsync({ isActive: false })}
              errorFallback="Could not update this student."
            />
          ) : (
            <button
              type="button"
              className="btn"
              disabled={save.isPending}
              onClick={() => save.mutate({ isActive: true })}
            >
              Put back on the roll
            </button>
          )}
          {student.isActive ? null : (
            <DeleteRecord
              noun="student"
              markCount={student.markCount}
              deleted={Boolean(student.deletedAt)}
              canRestore={canRestore}
              onDelete={() => remove.mutateAsync(true)}
              onRestore={() => remove.mutate(false)}
              pending={remove.isPending}
              error={remove.error ? message(remove.error, 'Could not change that.') : null}
            />
          )}
        </div>
      </div>

      <section>
        <SectionHeading
          title="Details"
          description="A student is taken off the roll rather than deleted, because their past attendance still has to add up."
        />
        <form
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate({
              firstName,
              lastName,
              guardianName: guardianName || null,
              guardianContact: guardianContact || null,
            });
          }}
        >
          {save.error ? (
            <div className="sm:col-span-2 lg:col-span-4">
              <ErrorNote message={message(save.error, 'Could not save.')} />
            </div>
          ) : null}
          <label className="grid gap-1.5">
            <span className="eyebrow">First name</span>
            <input
              className="field"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Last name</span>
            <input
              className="field"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Guardian</span>
            <input
              className="field"
              value={guardianName}
              onChange={(e) => setGuardianName(e.target.value)}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Guardian contact</span>
            <input
              className="field"
              value={guardianContact}
              onChange={(e) => setGuardianContact(e.target.value)}
            />
          </label>
          <div className="sm:col-span-2 lg:col-span-4">
            <button className="btn btn-primary" type="submit" disabled={!dirty || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
